import axios, { AxiosResponse } from 'axios';

// Get headers with authorization token and timezone
export async function getHeaders(): Promise<Record<string, string>> {
  const authToken = await localStorage.getItem("authToken");;
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const headers: Record<string, string> = {
    Timezone: timeZone,
  };
  
  if (authToken) {
    headers['Authorization'] = `Bearer ${authToken}`;
  }

  return headers;
}

// Unified API request function
export async function apiReq(
  endPoint: string,
  data: any,
  method: 'get' | 'post' | 'put' | 'delete',
  headers: Record<string, string> = {},
  requestOptions = {}
): Promise<any> {
  try {
    const tokenHeaders = await getHeaders();
    const combinedHeaders = {
      ...tokenHeaders,
      ...headers,
    };

    let response: AxiosResponse;

    if (method === 'get') {
      response = await axios.get(endPoint, {
        params: data,
        headers: combinedHeaders,
        ...requestOptions,
      });
    } else if (method === 'delete') {
      response = await axios.delete(endPoint, {
        data,
        headers: combinedHeaders,
        ...requestOptions,
      });
    } else {
      response = await axios[method](endPoint, data, {
        headers: combinedHeaders,
        ...requestOptions,
      });
    }

    // console.log('API Request:', { endPoint, method, payload: data, headers: combinedHeaders });
    // console.log('API Response:', response);

    return response.data as any;
  } catch (error: any) {
    console.error('API Error:', { endPoint, error: error.response ?? error });

    if (error.response?.status === 401) {
      return {
        status: 401,
        data: error.response,
      } as unknown as any;
    }

    return error.response ?? error;
  }
}

// Convenient methods wrapping apiReq
export function apiPost(endPoint: string, data?: any, headers?: Record<string, string>) {
  // 🚨 Check if data is FormData. If yes, DO NOT set application/json
  const isFormData = data instanceof FormData;
  const defaultHeaders = isFormData ? {} : { 'Content-Type': 'application/json' };
  
  const finalHeaders:any = { ...defaultHeaders, ...headers };
  
  return apiReq(endPoint, data, 'post', finalHeaders);
}

export function apiDelete(endPoint: string, data?: any, headers: Record<string, string> = {}) {
  return apiReq(endPoint, data, 'delete', headers);
}

export function apiGet(endPoint: string, data?: any, headers: Record<string, string> = {}, requestOptions: any = {}) {
  return apiReq(endPoint, data, 'get', headers, requestOptions);
}

export function apiPut(endPoint: string, data?: any, headers: Record<string, string> = {}) {
  return apiReq(endPoint, data, 'put', headers);
}

// Build query string from flat object, filtering empty/null/undefined values
export const buildQuery = (params: any): string => {
  return Object.entries(params)
    .filter(([_, value]: any) => value !== '' && value !== null && value !== undefined)
    .map(([key, value]: any) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
    .join('&');
};

// Recursive query string builder with support for nested objects and arrays
export const buildQueryString = (obj: any, prefix = ''): string => {
  return Object.entries(obj)
    .flatMap(([key, value]: any) => {
      const encodedKey = encodeURIComponent(prefix ? `${prefix}[${key}]` : key);

      if (value && typeof value === 'object' && !Array.isArray(value)) {
        // Nested object — recurse
        return buildQueryString(value, prefix ? `${prefix}[${key}]` : key);
      }

      if (Array.isArray(value)) {
        // Array, join with comma
        return `${encodedKey}=${encodeURIComponent(value.join(','))}`;
      }

      if (value !== '' && value !== null && value !== undefined) {
        return `${encodedKey}=${encodeURIComponent(value)}`;
      }

      return [];
    })
    .join('&');
};
